import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Pencil } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { usersApi } from "@/lib/api";
import { apiErrorMessage } from "@/lib/api/errors";
import type { UserDto } from "@/lib/api/types";
import { SHOP_ROLES, TENANT_ROLES, roleLabel, type Role } from "@/lib/auth/rbac";
import { isShop } from "@/lib/api/types";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";

/** Staff accounts for one restaurant: provision, edit role/password, activate/deactivate. */
export function StaffTab({
  tenantId,
  tenantName,
  businessType,
}: {
  tenantId: number;
  tenantName: string;
  businessType: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);

  const users = useQuery({
    queryKey: ["users", "tenant", tenantId],
    queryFn: () => usersApi.list(tenantId),
  });

  const toggleActive = useMutation({
    mutationFn: (user: UserDto) => usersApi.update(user.id, { active: !user.active }),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: `${updated.fullName} ${updated.active ? "activated" : "deactivated"}`,
        variant: "success",
      });
    },
    onError: (err) =>
      toast({ title: "Couldn't update user", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <>
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> New staff account
        </Button>
      </div>
      <Card>
        {users.isLoading && <Skeleton className="m-5 h-48" />}
        {users.data && users.data.length === 0 && (
          <EmptyState
            icon={Users}
            title="No staff yet"
            description={
              isShop(businessType)
                ? `Add the manager, supervisors and sellers for ${tenantName}.`
                : `Add the manager, waiters and kitchen accounts for ${tenantName}.`
            }
          />
        )}
        {users.data && users.data.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Username</TableHead>
                <TableHead className="hidden sm:table-cell">Full name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="hidden sm:table-cell">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-mono font-medium">
                    {user.username}
                    <div className="mt-1 font-sans text-xs font-normal text-muted-foreground sm:hidden">
                      {user.fullName} · {user.active ? "Active" : "Inactive"}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{user.fullName}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === "MANAGER" ? "default" : "secondary"}>{roleLabel(user.role, businessType)}</Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{formatDate(user.createdAt)}</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={user.active ? "success" : "destructive"}>
                      {user.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="space-y-1 text-right sm:space-x-2 sm:space-y-0 sm:whitespace-nowrap">
                    <Button variant="outline" size="sm" onClick={() => setEditing(user)}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <ConfirmDialog
                      trigger={
                        <Button variant="outline" size="sm" disabled={toggleActive.isPending}>
                          {user.active ? "Deactivate" : "Activate"}
                        </Button>
                      }
                      title={`${user.active ? "Deactivate" : "Activate"} ${user.fullName}?`}
                      description={
                        user.active
                          ? "They won't be able to sign in to the OSF app until reactivated."
                          : "They'll be able to sign in to the OSF app again."
                      }
                      confirmLabel={user.active ? "Deactivate" : "Activate"}
                      confirmVariant={user.active ? "destructive" : "default"}
                      onConfirm={() => toggleActive.mutateAsync(user).then(() => undefined)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && (setCreating(false), setEditing(null))}>
        {(creating || editing) && (
          <StaffDialog
            tenantId={tenantId}
            businessType={businessType}
            user={editing}
            onDone={() => {
              setCreating(false);
              setEditing(null);
            }}
          />
        )}
      </Dialog>
    </>
  );
}

function StaffDialog({
  tenantId,
  businessType,
  user,
  onDone,
}: {
  tenantId: number;
  businessType: string;
  user: UserDto | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [username, setUsername] = useState(user?.username ?? "");
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [role, setRole] = useState<Role>(user?.role ?? "WAITER");
  const [password, setPassword] = useState("");

  const save = useMutation({
    mutationFn: () => {
      if (user) {
        return usersApi.update(user.id, {
          fullName: fullName.trim(),
          role,
          ...(password ? { password } : {}),
        });
      }
      return usersApi.create({
        tenantId,
        username: username.trim(),
        password,
        fullName: fullName.trim(),
        role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({ title: user ? "User updated" : "Staff account created", variant: "success" });
      onDone();
    },
    onError: (err) => toast({ title: "Couldn't save user", description: apiErrorMessage(err), variant: "destructive" }),
  });

  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>{user ? `Edit ${user.username}` : "New staff account"}</DialogTitle>
      </DialogHeader>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label>Username</Label>
          <Input
            autoFocus={!user}
            required
            disabled={!!user}
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            placeholder="e.g. asha"
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Full name</Label>
          <Input required value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Emma" />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={role} onValueChange={(v) => setRole(v as Role)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(isShop(businessType) ? SHOP_ROLES : TENANT_ROLES).map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r, businessType)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{user ? "New password (leave blank to keep)" : "Password"}</Label>
          <Input
            type="password"
            required={!user}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>
        <DialogFooter>
          <Button
            type="submit"
            disabled={save.isPending || !fullName.trim() || (!user && (!username.trim() || !password))}
          >
            {save.isPending ? "Saving…" : user ? "Save changes" : "Create account"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
